/**
 * The side panel — `docs/activity.md` §6.1, §6.7, §6.8 (Board 2): the month
 * (Sunday first, the shown range tinted, today solid), **Calendars** with
 * **+ Add**, **Show**'s four switches, and **To book**.
 *
 * Each person under *Calendars* has a ⋯ (Aaron, Oct 24, note 1.5: "The people
 * that show up under calendars on the left side should be able to be removed
 * so they aren't permanently part of that list"): **Show only <them>**, and
 * **Remove from this list** — remembered for this device, like the rest of the
 * page (`calendar.hidden`), and undone by showing them again. Your own row has
 * no Remove: the list always starts with you.
 */
import { useEffect, useRef, useState } from 'react'
import { WEEKDAY_LETTERS, firstNameOf, monthHeading, monthWeeks, stepView, toneOf, type ShowState } from '@rowted/shared'
import type { MenuItem } from '../../components/context-menu.js'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { Face } from '../faces.js'
import { useActivity } from '../activity-context.js'
import { CalendarMenu, isMenuKey, menuAtElement, type MenuAt } from './item-menu.js'

export interface ToBookCard {
  kind: 'appointment' | 'job'
  jobId: string
  appointmentId: string | null
  title: string
  propertyName: string | null
  city: string | null
  state: string | null
  personId: string
}

function Head({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="mb-2 mt-4 flex items-center text-[10.5px] font-semibold uppercase tracking-[.06em] text-text-faint">
      {children}<span className="flex-1" />{right}
    </div>
  )
}

/** A 28 × 16 switch (Board 2). */
export function MiniSwitch({ on }: { on: boolean }) {
  return (
    <span className={cn('inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5', on ? 'justify-end bg-accent' : 'justify-start bg-line-strong')}>
      <i className="block h-3 w-3 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.25)]" />
    </span>
  )
}

const SHOW_ROWS: { key: keyof ShowState; label: string }[] = [
  { key: 'visits', label: 'Site visits & meetings' },
  { key: 'tasks', label: 'Tasks with a time' },
  { key: 'blocks', label: 'Time blocks' },
  { key: 'drives', label: 'Drive times' },
]

export function SidePanel({
  date, shown, today, toBook, onDate, onAdd, onToBookDragStart,
}: {
  date: string
  /** The days the grid shows, for the month's tint. */
  shown: string[]
  today: string
  toBook: ToBookCard[]
  onDate: (date: string) => void
  onAdd: () => void
  onToBookDragStart: (card: ToBookCard, event: React.DragEvent) => void
}) {
  const { state, change, directory, me, people } = useActivity()
  /*
   * The month pages on its own (Aaron, Oct 23): its arrows look a month back or
   * on and never move the board; a day pressed on it does. It follows the board
   * whenever the board's own date moves.
   */
  const [peek, setPeek] = useState(date)
  useEffect(() => { setPeek(date) }, [date])
  const month = peek
  const monthNumber = peek.slice(0, 7)
  const shownSet = new Set(shown)
  const byId = new Map((directory?.people ?? []).map((p) => [p.id, p]))
  /* You, each person on, then up to three of recentPeople not on (§6.1). */
  /* …and, while there are fewer than three, the people seen by a grant (Board 2: *Hannah Brooks · you can see*). */
  /* …less anyone taken off it with their ⋯ (note 1.5). */
  const hidden = new Set(state.calendar.hidden)
  const recent = state.recentPeople.filter((id) => id !== me.user.id && !people.includes(id) && !hidden.has(id))
  const granted = (directory?.people ?? []).filter((p) => p.seenBy === 'grant' && !people.includes(p.id)
    && !recent.includes(p.id) && !hidden.has(p.id)).map((p) => p.id)
  const rows = [me.user.id, ...people.filter((id) => id !== me.user.id), ...[...recent, ...granted].slice(0, 3)]
    .map((id) => byId.get(id)).filter((p): p is NonNullable<typeof p> => Boolean(p))

  const togglePerson = (id: string) => {
    const on = people.includes(id)
    const ids = on ? people.filter((other) => other !== id) : [...people, id]
    change({ people: { ids: ids.length > 0 ? ids : [me.user.id], unassigned: state.people.unassigned } })
  }

  /* A person's ⋯ (note 1.5). */
  const [menu, setMenu] = useState<{ id: string; at: MenuAt } | null>(null)
  const menuOpen = useRef<string | null>(null)
  const showOnly = (id: string) => {
    change({
      people: { ids: [id], unassigned: [] },
      ...(id === me.user.id ? {} : { recentPeople: [id, ...state.recentPeople.filter((other) => other !== id)].slice(0, 8) }),
    })
  }
  const removeFromList = (id: string) => {
    const ids = people.filter((other) => other !== id)
    change({
      ...(people.includes(id) ? { people: { ids: ids.length > 0 ? ids : [me.user.id], unassigned: state.people.unassigned } } : {}),
      recentPeople: state.recentPeople.filter((other) => other !== id),
      calendar: { hidden: [...state.calendar.hidden.filter((other) => other !== id), id] },
    })
  }
  const menuFor = (id: string): MenuItem[] => {
    const person = byId.get(id)
    const mine = id === me.user.id
    return [
      { key: 'only', label: mine ? 'Show only mine' : `Show only ${firstNameOf(person?.name ?? '')}`, onSelect: () => showOnly(id) },
      ...(mine ? [] : [{ key: 'remove', label: 'Remove from this list', tone: 'danger' as const, separatorBefore: true,
        sub: 'add them again with + Add', onSelect: () => removeFromList(id) }]),
    ]
  }

  return (
    /* The boards' panel reads at `line-height: normal` (Board 2's To book card is 78 tall, not 89). */
    <aside className="w-[248px] shrink-0 self-stretch border-r border-line bg-surface px-4 py-3.5 leading-[normal]">
      <div className="flex items-center gap-1.5">
        <b className="text-[13px]">{monthHeading(month)}</b>
        <span className="flex-1" />
        <button type="button" aria-label="Last month" className="text-text-muted" onClick={() => setPeek(stepView('month', month, -1))}>
          <Glyph name="chevronLeft" size={14} />
        </button>
        <button type="button" aria-label="Next month" className="text-text-muted" onClick={() => setPeek(stepView('month', month, 1))}>
          <Glyph name="chevronRight" size={14} />
        </button>
      </div>
      <div className="mt-2 grid grid-cols-7 gap-0.5">
        {WEEKDAY_LETTERS.map((letter, index) => (
          <span key={index} className="text-center text-[10px] font-semibold text-text-faint">{letter}</span>
        ))}
        {monthWeeks(month).flat().map((day) => {
          const outside = day.slice(0, 7) !== monthNumber
          const isToday = day === today
          const inRange = shownSet.has(day)
          return (
            <button key={day} type="button" onClick={() => onDate(day)}
              className={cn('h-[22px] rounded-md text-center text-[11px] leading-[22px]',
                outside ? 'text-text-faint opacity-60'
                  : isToday ? 'bg-accent font-bold text-white'
                    : inRange ? 'bg-accent-soft font-semibold text-accent-dark'
                      : day < today ? 'text-text-muted' : 'text-text-body')}>
              {Number(day.slice(8))}
            </button>
          )
        })}
      </div>

      <Head right={(
        <button type="button" onClick={onAdd} className="inline-flex items-center gap-[3px] text-[12px] normal-case tracking-normal text-accent">
          <Glyph name="plus" size={12} />Add
        </button>
      )}>Calendars</Head>
      {rows.map((person) => {
        const on = people.includes(person.id)
        const edge = toneOf(person.colour).edge
        return (
          <div key={person.id} data-calendar-row={person.id} className="group flex h-8 w-full items-center gap-1">
            <button type="button" role="checkbox" aria-checked={on} onClick={() => togglePerson(person.id)}
              onContextMenu={(event) => {
                event.preventDefault()
                /* The key's own `contextmenu` after Shift+F10 finds this menu open and leaves it be. */
                if (menuOpen.current === person.id) return
                const keyed = event.clientX === 0 && event.clientY === 0
                menuOpen.current = person.id
                setMenu({ id: person.id, at: keyed ? menuAtElement(event.currentTarget) : { x: event.clientX, y: event.clientY } })
              }}
              onKeyDown={(event) => {
                if (!isMenuKey(event)) return
                event.preventDefault()
                menuOpen.current = person.id
                setMenu({ id: person.id, at: menuAtElement(event.currentTarget) })
              }}
              className="flex h-8 min-w-0 flex-1 items-center gap-2 text-left text-[12.5px]">
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] text-white"
                style={{ borderColor: edge, background: on ? edge : 'rgb(var(--color-surface))' }}>
                {on && <Glyph name="check" size={11} stroke={3} />}
              </span>
              <Face person={person} size={22} />
              <span className="flex min-w-0 flex-col">
                {/* Ticked reads 600, unticked 500 — Alex's own unticked row too (Boards 3, 18). */}
                <span className={cn('truncate text-text', on ? 'font-semibold' : 'font-medium')}>
                  {person.name}{person.id === me.user.id ? ' (you)' : ''}
                </span>
                {person.seenBy === 'grant' && <span className="text-[10.5px] text-text-faint">you can see</span>}
              </span>
            </button>
            {/* Aaron's three dots on the right (note 1.5): faint until the row is pointed at or the dots have the focus. */}
            <button type="button" aria-label={`More for ${person.name}`} aria-haspopup="menu"
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect()
                menuOpen.current = person.id
                setMenu({ id: person.id, at: { x: rect.left, y: rect.bottom + 4 } })
              }}
              className={cn('inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-text-faint hover:bg-hover hover:text-text',
                'opacity-60 group-hover:opacity-100 focus-visible:opacity-100', menu?.id === person.id && 'bg-hover opacity-100')}>
              <Glyph name="more" size={14} />
            </button>
          </div>
        )
      })}
      {menu && (
        <CalendarMenu items={menuFor(menu.id)} at={menu.at} label={`${byId.get(menu.id)?.name ?? 'Calendar'} — more`}
          onClose={() => { menuOpen.current = null; setMenu(null) }} />
      )}

      <Head>Show</Head>
      {SHOW_ROWS.map((row) => (
        <button key={row.key} type="button" role="switch" aria-checked={state.calendar.show[row.key]}
          onClick={() => change({ calendar: { show: { [row.key]: !state.calendar.show[row.key] } } })}
          className="flex h-[30px] w-full items-center gap-2 text-left text-[12.5px] text-text">
          <MiniSwitch on={state.calendar.show[row.key]} />{row.label}
        </button>
      ))}

      {toBook.length > 0 && (
        <>
          <Head>To book · {toBook.length}</Head>
          <div className="flex flex-col gap-2">
            {toBook.map((card) => (
              <div key={card.appointmentId ?? card.jobId} draggable
                onDragStart={(event) => onToBookDragStart(card, event)}
                className="cursor-grab rounded-[10px] border border-dashed border-line-strong bg-surface px-2.5 py-[9px]">
                <div className="flex items-start gap-1.5">
                  <span className="text-text-faint"><Glyph name="grip" size={12} /></span>
                  <div className="flex min-w-0 flex-col">
                    <b className="text-[12.5px]">{card.title}</b>
                    <span className="text-[11px] text-text-muted">
                      {card.propertyName}{card.city ? ' · ' : ''}
                      {card.city && <span className="whitespace-nowrap">{card.city}{card.state ? `, ${card.state}` : ''}</span>}
                    </span>
                    <span className="mt-0.5 text-[10.5px] text-text-faint">drag onto the calendar</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </aside>
  )
}
